import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  CalendarClock as CalendarIcon,
  CalendarDays as CalendarDaysIcon,
  ChevronLeft as CaretLeftIcon,
  ChevronRight as CaretRightIcon,
  CircleAlert as CircleAlertIcon,
  Clock as ClockIcon,
  Cpu as CpuIcon
} from 'lucide-react'
import {
  useGetActiveSchedulesQuery,
  useGetPlantScheduleGatewaysQuery,
  usePlantSessionPaint,
  type ActiveHoliday,
  type ActiveScheduleProfile,
  type ActiveSchedulesResponse,
  type PlantScheduleGateway
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { StatusDot } from '../../components/primitives/StatusDot'
import { cn } from '../../lib/utils'
import { PlantHref } from './PlantHref'
import {
  SessionGhostRow,
  SessionMarkAffordances,
  SessionRollupPill,
  SessionRowEndMark,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

const ACTIVE_POLL_MS = 30_000

/**
 * Default rows per page. The Schedules tab can carry hundreds of
 * profiles on a real Ignition gateway, so paginate aggressively while
 * keeping every active row reachable on the first page (we always sort
 * active-first, so the user typically doesn't need to leave page 1).
 */
const SCHEDULES_PAGE_SIZE = 25
const HOLIDAYS_PAGE_SIZE = 25

/**
 * `/plant/schedules` — Schedules tab landing.
 *
 * Lists every `:Compute` (gateway) that owns at least one
 * `:ScheduleProfile` or `:Holiday`. For each gateway the page polls
 * `POST /api/ontology/schedules/active`, which reads the registered
 * `[System]Gateway/CurrentDateTime` tag by node ID and runs the pure
 * `evaluateActiveSchedules` library, so every schedule/holiday gets
 * a live "active right now" status dot.
 *
 * Gateways with no `url` set on the `:Compute` node (e.g. partial
 * ingests) render an inert tile that explains why the active-status
 * pull can't run for them.
 *
 * Session paint marks live rows from the write log; deleted
 * profiles/holidays render as ghost rows under their gateway and are
 * never sourced from the active-status poll.
 *
 * Profile/holiday rows deep-link to `/plant/schedules/:nodeId`
 * (see `plantSchedulesNodeHref`) so detail stays under this tab.
 */
const SchedulesPage = (): React.ReactElement => {
  const { data, isLoading, isError } = useGetPlantScheduleGatewaysQuery()
  const gateways = data ?? []

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Schedules</h1>
        <p className="mt-1 text-sm text-ink-3">{schedulesSubtitle(gateways)}</p>
      </header>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load schedule gateways"
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading && gateways.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading schedules…</div>
        </div>
      ) : gateways.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="No schedules yet"
            hint="Ingest an Ignition gateway that defines schedules or holidays to populate this tab."
            icon={CalendarIcon}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-card">
          {gateways.map((gateway) => (
            <GatewayCard key={gateway.id} gateway={gateway} />
          ))}
        </div>
      )}
    </div>
  )
}

function GatewayCard({ gateway }: { gateway: PlantScheduleGateway }): React.ReactElement {
  const hasUrl = gateway.url.length > 0
  const { data, isFetching, isError } = useGetActiveSchedulesQuery(
    {
      gateway_url: gateway.url,
      project_name: gateway.project_name ?? undefined
    },
    {
      skip: !hasUrl,
      pollingInterval: ACTIVE_POLL_MS,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true
    }
  )
  const scheduleGhosts = useVisibleGhosts(gateway.id, 'ScheduleProfile')
  const holidayGhosts = useVisibleGhosts(gateway.id, 'Holiday')

  const showBody = (hasUrl && !isError) || scheduleGhosts.length > 0 || holidayGhosts.length > 0

  return (
    <section className="rounded-xl border bg-card p-card text-card-foreground shadow-sm">
      <GatewayHeader
        gateway={gateway}
        active={hasUrl && !isError ? data : undefined}
        isFetching={isFetching}
        isError={isError}
      />
      {!hasUrl ? (
        <Empty
          title="Gateway URL not set"
          hint="This compute host was ingested without a URL, so the live active-status pull can't run."
          icon={CircleAlertIcon}
        />
      ) : isError ? (
        <Empty
          title="Couldn't read live status"
          hint="This gateway has no registered [System]Gateway/CurrentDateTime tag or the read failed; ask an admin to re-ingest the gateway backup and check the tag on the Tags page."
          icon={WarningCircleIcon}
        />
      ) : null}
      {showBody ? (
        <GatewayBody
          gateway={gateway}
          active={hasUrl && !isError ? data : undefined}
          scheduleGhosts={scheduleGhosts}
          holidayGhosts={holidayGhosts}
        />
      ) : null}
    </section>
  )
}

function GatewayHeader({
  gateway,
  active,
  isFetching,
  isError
}: {
  gateway: PlantScheduleGateway
  active: ActiveSchedulesResponse | undefined
  isFetching: boolean
  isError: boolean
}): React.ReactElement {
  const activeScheduleCount = active
    ? active.schedule_profiles.filter((s) => s.active).length
    : null
  const activeHolidayCount = active ? active.holidays.filter((h) => h.active).length : null
  const { markFor, rollupByContainerId, touchedRollupByContainerId, deletedRollupByContainerId } =
    usePlantSessionPaint()
  const mark = markFor(gateway.id)
  const added = rollupByContainerId.get(gateway.id) ?? 0
  const modified = touchedRollupByContainerId.get(gateway.id) ?? 0
  const deleted = deletedRollupByContainerId.get(gateway.id) ?? 0

  return (
    <header className="mb-3 flex flex-wrap items-baseline gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
        <CpuIcon size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <h2 className="m-0 min-w-0 flex-1 truncate text-base font-semibold text-ink">
            {gateway.name}
          </h2>
          <SessionMarkAffordances mark={mark} />
          <SessionRollupPill added={added} modified={modified} deleted={deleted} />
        </div>
        <div className="mt-0.5 truncate text-12 text-ink-3">
          {gateway.url || 'no URL on file'}
          {active ? (
            <>
              {' · '}
              <span title={active.gateway_time_quality}>
                gateway clock {formatGatewayTime(active.gateway_time)}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {(activeScheduleCount ?? 0) + (activeHolidayCount ?? 0) > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-st-info-bg px-2.5 py-1 text-12 font-semibold uppercase tracking-wide text-st-info">
            <StatusDot status="info" />
            {(activeScheduleCount ?? 0) + (activeHolidayCount ?? 0)} active now
          </span>
        )}
        <Badge variant="outline">
          {gateway.scheduleProfileCount}{' '}
          {gateway.scheduleProfileCount === 1 ? 'schedule' : 'schedules'}
        </Badge>
        <Badge variant="outline">
          {gateway.holidayCount} {gateway.holidayCount === 1 ? 'holiday' : 'holidays'}
        </Badge>
        {isFetching && !isError && active && (
          <span className="text-11 text-ink-3" aria-live="polite">
            refreshing…
          </span>
        )}
      </div>
    </header>
  )
}

function GatewayBody({
  gateway,
  active,
  scheduleGhosts,
  holidayGhosts
}: {
  gateway: PlantScheduleGateway
  active: ActiveSchedulesResponse | undefined
  scheduleGhosts: PlantSessionGhostView[]
  holidayGhosts: PlantSessionGhostView[]
}): React.ReactElement {
  // Active rows surface to the top, then by-name within each partition,
  // so a 180-profile gateway still reads at-a-glance.
  const schedules = React.useMemo(() => sortActiveFirst(active?.schedule_profiles ?? []), [active])
  const holidays = React.useMemo(() => sortActiveFirst(active?.holidays ?? []), [active])
  const hasSchedules = gateway.scheduleProfileCount > 0 || scheduleGhosts.length > 0
  const hasHolidays = gateway.holidayCount > 0 || holidayGhosts.length > 0
  const activeScheduleCount = schedules.filter((s) => s.active).length
  const activeHolidayCount = holidays.filter((h) => h.active).length

  const [schedulesPage, setSchedulesPage] = React.useState(0)
  const [holidaysPage, setHolidaysPage] = React.useState(0)

  // Pagination stays on the live poll list only — ghosts append after the page.
  const scheduleTotalPages = Math.max(1, Math.ceil(schedules.length / SCHEDULES_PAGE_SIZE))
  const holidayTotalPages = Math.max(1, Math.ceil(holidays.length / HOLIDAYS_PAGE_SIZE))

  // Clamp the active page when the underlying list shrinks (e.g. after
  // a refetch returns fewer rows). Cheap to recompute since totals are
  // small ints.
  React.useEffect(() => {
    if (schedulesPage > scheduleTotalPages - 1) setSchedulesPage(scheduleTotalPages - 1)
  }, [schedulesPage, scheduleTotalPages])
  React.useEffect(() => {
    if (holidaysPage > holidayTotalPages - 1) setHolidaysPage(holidayTotalPages - 1)
  }, [holidaysPage, holidayTotalPages])

  const scheduleSlice = React.useMemo(
    () =>
      schedules.slice(
        schedulesPage * SCHEDULES_PAGE_SIZE,
        (schedulesPage + 1) * SCHEDULES_PAGE_SIZE
      ),
    [schedules, schedulesPage]
  )
  const holidaySlice = React.useMemo(
    () =>
      holidays.slice(holidaysPage * HOLIDAYS_PAGE_SIZE, (holidaysPage + 1) * HOLIDAYS_PAGE_SIZE),
    [holidays, holidaysPage]
  )

  return (
    <div className="grid grid-cols-1 gap-card lg:grid-cols-2">
      {hasSchedules && (
        <SubSection
          title="Schedule profiles"
          icon={ClockIcon}
          total={schedules.length || gateway.scheduleProfileCount}
          activeCount={schedules.length === 0 ? null : activeScheduleCount}
          page={schedulesPage}
          pageSize={SCHEDULES_PAGE_SIZE}
          totalPages={scheduleTotalPages}
          onPrev={() => setSchedulesPage((p) => Math.max(0, p - 1))}
          onNext={() => setSchedulesPage((p) => Math.min(scheduleTotalPages - 1, p + 1))}
          showPagination={schedules.length > SCHEDULES_PAGE_SIZE}
        >
          {schedules.length === 0 && scheduleGhosts.length === 0 ? (
            <SubSectionSkeleton label="Loading schedule status…" />
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {scheduleSlice.map((s) => (
                <ScheduleRow key={s.id} schedule={s} />
              ))}
              {scheduleGhosts.map((ghost) => (
                <li key={ghost.id}>
                  <SessionGhostRow name={ghost.name ?? ghost.id} view={ghost.view} />
                </li>
              ))}
            </ul>
          )}
        </SubSection>
      )}
      {hasHolidays && (
        <SubSection
          title="Holidays"
          icon={CalendarDaysIcon}
          total={holidays.length || gateway.holidayCount}
          activeCount={holidays.length === 0 ? null : activeHolidayCount}
          page={holidaysPage}
          pageSize={HOLIDAYS_PAGE_SIZE}
          totalPages={holidayTotalPages}
          onPrev={() => setHolidaysPage((p) => Math.max(0, p - 1))}
          onNext={() => setHolidaysPage((p) => Math.min(holidayTotalPages - 1, p + 1))}
          showPagination={holidays.length > HOLIDAYS_PAGE_SIZE}
        >
          {holidays.length === 0 && holidayGhosts.length === 0 ? (
            <SubSectionSkeleton label="Loading holiday status…" />
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {holidaySlice.map((h) => (
                <HolidayRow key={h.id} holiday={h} />
              ))}
              {holidayGhosts.map((ghost) => (
                <li key={ghost.id}>
                  <SessionGhostRow name={ghost.name ?? ghost.id} view={ghost.view} />
                </li>
              ))}
            </ul>
          )}
        </SubSection>
      )}
    </div>
  )
}

function SubSection({
  title,
  icon: Icon,
  total,
  activeCount,
  page,
  pageSize,
  totalPages,
  showPagination,
  onPrev,
  onNext,
  children
}: {
  title: string
  icon: React.ComponentType<{ size?: number | string; className?: string }>
  total: number
  /** Null while we haven't loaded live status yet. */
  activeCount: number | null
  page: number
  pageSize: number
  totalPages: number
  showPagination: boolean
  onPrev: () => void
  onNext: () => void
  children: React.ReactNode
}): React.ReactElement {
  return (
    <section>
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <Icon size={14} className="text-ink-3" />
        <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">
          {title}
        </h3>
        <span className="text-xs text-ink-3">
          {total} {total === 1 ? 'item' : 'items'}
        </span>
        {activeCount != null &&
          (activeCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-st-info-bg px-2 py-0.5 text-11 font-semibold uppercase tracking-wide text-st-info">
              <StatusDot status="info" />
              {activeCount} active
            </span>
          ) : (
            <span className="text-11 text-ink-3">none active</span>
          ))}
        {showPagination && (
          <PageRangeLabel page={page} pageSize={pageSize} total={total} totalPages={totalPages} />
        )}
      </header>
      {children}
      {showPagination && (
        <Pagination page={page} totalPages={totalPages} onPrev={onPrev} onNext={onNext} />
      )}
    </section>
  )
}

function PageRangeLabel({
  page,
  pageSize,
  total,
  totalPages
}: {
  page: number
  pageSize: number
  total: number
  totalPages: number
}): React.ReactElement {
  const start = page * pageSize + 1
  const end = Math.min(total, (page + 1) * pageSize)
  return (
    <span className="ml-auto whitespace-nowrap text-11 text-ink-4">
      {start}–{end} of {total} · page {page + 1}/{totalPages}
    </span>
  )
}

function Pagination({
  page,
  totalPages,
  onPrev,
  onNext
}: {
  page: number
  totalPages: number
  onPrev: () => void
  onNext: () => void
}): React.ReactElement {
  const atFirst = page <= 0
  const atLast = page >= totalPages - 1
  return (
    <div className="mt-2 flex items-center justify-end gap-2" aria-label="Pagination">
      <PageButton disabled={atFirst} onClick={onPrev} label="Prev" icon="left" />
      <PageButton disabled={atLast} onClick={onNext} label="Next" icon="right" />
    </div>
  )
}

function PageButton({
  disabled,
  onClick,
  label,
  icon
}: {
  disabled: boolean
  onClick: () => void
  label: string
  icon: 'left' | 'right'
}): React.ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex items-center gap-1 rounded-md border px-2.5 py-1 text-11 transition-colors',
        disabled
          ? 'cursor-not-allowed border-line text-ink-4'
          : 'cursor-pointer border-line text-ink-2 hover:bg-bg-sunk hover:text-ink focus-visible:border-brand focus-visible:outline-none'
      )}
    >
      {icon === 'left' && <CaretLeftIcon size={10} />}
      {label}
      {icon === 'right' && <CaretRightIcon size={10} />}
    </button>
  )
}

function sortActiveFirst<T extends { active: boolean; name: string }>(items: readonly T[]): T[] {
  const next = items.slice()
  next.sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
  return next
}

function SubSectionSkeleton({ label }: { label: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-dashed bg-background px-3 py-2 text-xs text-ink-3">
      {label}
    </div>
  )
}

function ScheduleRow({ schedule }: { schedule: ActiveScheduleProfile }): React.ReactElement {
  const isComposite = schedule.schedule_type === 'composite schedule'
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(schedule.id)
  return (
    <li data-active={schedule.active}>
      <PlantHref
        id={schedule.id}
        label="ScheduleProfile"
        className={sessionMarkClass(
          cn(
            'flex items-center gap-2 rounded-lg border bg-background px-3 py-2 no-underline transition-colors',
            'hover:bg-accent hover:text-accent-foreground',
            // Live "active now" uses info blue — green/amber are reserved for session paint.
            schedule.active && !mark && 'border-st-info/40 bg-st-info-bg shadow-sm'
          ),
          mark
        )}
      >
        <StatusDot status={schedule.active ? 'info' : 'idle'} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className={cn(
                'truncate text-sm font-medium',
                schedule.active && !mark ? 'text-st-info' : 'text-ink'
              )}
            >
              {schedule.name}
            </span>
            <SessionRowEndMark mark={mark} />
            {schedule.active && <ActiveNowPill kind="schedule" />}
            <Badge variant="outline">{isComposite ? 'composite' : 'basic'}</Badge>
            {schedule.observe_holidays && <Badge variant="outline">observes holidays</Badge>}
            {schedule.repeat_mode && schedule.repeat_mode !== 'Off' && (
              <Badge variant="ghost">{schedule.repeat_mode}</Badge>
            )}
          </div>
          {schedule.description && (
            <div className="truncate text-12 text-ink-3">{schedule.description}</div>
          )}
          {schedule.reason && (
            <div
              className="mt-0.5 truncate text-12 text-ink-3"
              title={schedule.reason}
              aria-label={`Status reason: ${schedule.reason}`}
            >
              {schedule.reason}
            </div>
          )}
          {isComposite && schedule.sub_schedules.length > 0 && (
            <div className="mt-0.5 truncate text-12 text-ink-3">
              <span className="font-medium">composes:</span> {schedule.sub_schedules.join(', ')}
            </div>
          )}
        </div>
      </PlantHref>
    </li>
  )
}

function HolidayRow({ holiday }: { holiday: ActiveHoliday }): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(holiday.id)
  return (
    <li data-active={holiday.active}>
      <PlantHref
        id={holiday.id}
        label="Holiday"
        className={sessionMarkClass(
          cn(
            'flex items-center gap-2 rounded-lg border bg-background px-3 py-2 no-underline transition-colors',
            'hover:bg-accent hover:text-accent-foreground',
            holiday.active && !mark && 'border-st-info/40 bg-st-info-bg shadow-sm'
          ),
          mark
        )}
      >
        <StatusDot status={holiday.active ? 'info' : 'idle'} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span
              className={cn(
                'truncate text-sm font-medium',
                holiday.active && !mark ? 'text-st-info' : 'text-ink'
              )}
            >
              {holiday.name}
            </span>
            <SessionRowEndMark mark={mark} />
            {holiday.active && <ActiveNowPill kind="holiday" />}
            {holiday.repeat_annually && <Badge variant="outline">annual</Badge>}
          </div>
          {holiday.date && (
            <div className="truncate text-12 text-ink-3">{formatHolidayDate(holiday.date)}</div>
          )}
        </div>
      </PlantHref>
    </li>
  )
}

function ActiveNowPill({ kind }: { kind: 'schedule' | 'holiday' }): React.ReactElement {
  const isHoliday = kind === 'holiday'
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-st-info px-2 py-0.5 text-11 font-semibold uppercase tracking-wide text-white">
      {isHoliday ? 'Holiday today' : 'Active now'}
    </span>
  )
}

function schedulesSubtitle(gateways: readonly PlantScheduleGateway[]): string {
  if (gateways.length === 0) {
    return 'Live schedule and holiday status, joined with the gateway clock.'
  }
  let schedules = 0
  let holidays = 0
  for (const g of gateways) {
    schedules += g.scheduleProfileCount
    holidays += g.holidayCount
  }
  const segments = [
    `${gateways.length} ${gateways.length === 1 ? 'gateway' : 'gateways'}`,
    `${schedules} ${schedules === 1 ? 'schedule' : 'schedules'}`
  ]
  if (holidays > 0) {
    segments.push(`${holidays} ${holidays === 1 ? 'holiday' : 'holidays'}`)
  }
  return `${segments.join(' · ')}.`
}

function formatGatewayTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString()
}

// Holiday dates from Ignition are timezone-naive ISO strings. Render the
// date in UTC so we don't shift "Christmas 2026-12-25" into the day before
// for users west of UTC.
function formatHolidayDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export default SchedulesPage
